import { Component, EventEmitter, Input, Output } from '@angular/core';
import { List as ImmutableList, List } from 'immutable';
import { debounce } from 'lodash-decorators';
import * as constants from '../../constants-types/constants';
import {
    FilterInfo,
    FilterType,
    Group,
    IconSize,
    IconType,
    Person,
    SearchResult,
    TypesUtility
} from '../../core-data-model';
import {
    GroupsDataService,
    SearchApi,
    SearchStateFactory
} from '../../core-services';
import { TranslationService } from '../../translations';

@Component({
    selector: 'add-to-group-pillbox',
    host: { style: 'width:100%' },
    template: `
        <ok-pillbox-with-typeahead
            [(ngModel)]="groupSet"
            [typeaheadList]="possibilities"
            [typeaheadPlaceholder]="TR.key('teams.membersplaceholder')"
            [searchStyling]="true"
            [isCreatingGroup]="isCreatingGroup"
            [isLoadingPossibilities]="loadingPossibilities"
            [isDisabled]="isProcessing"
            [disablePropagation]="disablePropagation"
            (typeaheadOnTextChange)="updatePeoplePossibilities($event)"
            (onAdd)="addResult($event)"
            (onRemove)="onRemove.emit($event)"
        >
            <ng-template #pillbox let-item="item">
                @if (showReferenceProfile(item)) {
                    @if (item.referencePattern) {
                        <abstract-icon
                            [iconType]="
                                item.referencePattern.icon.abstractIconSVG
                            "
                            [iconSize]="IconSize.M_large"
                            style="margin-right: 6px;border: 3px solid white;border-radius: 50%;background-color: white;"
                            title="{{
                                TR.key(item.referencePattern.key) | titlecase
                            }}"
                        >
                        </abstract-icon>
                    }
                }
                @if (!showReferenceProfile(item)) {
                    <abstract-icon
                        [iconType]="IconType.Hourglass"
                        [iconSize]="IconSize.Medium_25"
                        title="{{ TR.key('label.common-terms.no.pattern') }}"
                        style="fill: white;"
                    >
                    </abstract-icon>
                }
                <span>
                    {{ pillboxDisplayFunction(item) }}
                </span>
            </ng-template>
            <ng-template #typeahead let-item="item">
                {{ typeaheadDisplayFunction(item) }}
            </ng-template>
        </ok-pillbox-with-typeahead>
    `,
    standalone: false
})
export class AddToGroupPillboxComponent {
    IconSize = IconSize;

    @Input()
    groupSet: List<SearchResult> = ImmutableList();
    @Input()
    isProcessing = false;
    @Input()
    isCreatingGroup: boolean;
    @Input()
    disablePropagation = false;
    @Output()
    onAdded = new EventEmitter();
    @Output()
    onRemove = new EventEmitter();
    @Output()
    resetBoundValue = new EventEmitter();

    possibilities: List<SearchResult> = ImmutableList();
    loadingPossibilities = false;
    IconType = IconType;

    constructor(
        public TR: TranslationService,
        private groups: GroupsDataService,
        private searchApi: SearchApi,
        private searchStateFactory: SearchStateFactory,
        public type: TypesUtility
    ) {}

    typeaheadDisplayFunction(item: SearchResult) {
        if (this.type.isGroup(item)) {
            return this.groupDisplayText(item);
        } else if (this.type.isPerson(item)) {
            return `${
                item.email
            } (${item.fullName()}) ${this.getAdditionalItemText(item)}`;
        } else if (this.type.isJob(item)) {
            return item.title;
        }
    }

    showReferenceProfile(item: SearchResult) {
        return item instanceof Person && item.hasBehavioralPattern();
    }

    pillboxDisplayFunction(item: SearchResult) {
        if (this.type.isGroup(item)) {
            return this.groupDisplayText(item);
        } else if (this.type.isPerson(item)) {
            return item.fullName();
        } else if (this.type.isJob(item)) {
            return item.title;
        }
    }

    groupDisplayText(group: Group) {
        return `${group.name} [ ${this.TR.key('common.team')} ]`;
    }

    getAdditionalItemText(groupOrPeople: Person) {
        if (!groupOrPeople.hasBehavioralPattern()) {
            return `[ ${this.TR.key('label.common-terms.no.pattern')} ]`;
        }
        return '';
    }

    async addResult(event: SearchResult) {
        if (event instanceof Group) {
            await this.addGroup(event);
        } else {
            if (!this.inGroupSetAlready(event)) {
                this.onAdded.emit(event);
            }
        }
    }

    private async addGroup(group: Group) {
        let people = await this.groups.getGroupContentsByGroup(group);
        people = people.filter((person) => !this.inGroupSetAlready(person));
        if (people.length < 1) {
            this.resetBoundValue.emit();
        } else {
            people.forEach((person) => this.onAdded.emit(person));
        }
    }

    updatePeoplePossibilities(filterText: string | null): Promise<any> {
        if (!filterText || filterText.trim() === '') {
            this.possibilities = ImmutableList();
            return;
        }

        this.loadingPossibilities = true;
        return this.runDebouncedPeopleAndJobsSearch(filterText);
    }

    @debounce(constants.peopleSearchDelay)
    private runDebouncedPeopleAndJobsSearch(
        filterText: string | null
    ): Promise<any> {
        const matchedGroups: Group[] =
            this.groups.getGroupsWithNameMatch(filterText);

        const search = this.searchStateFactory
            .newSearchState()
            .setPageSize(5)
            .setFilter(
                new FilterInfo({
                    type: FilterType.Search,
                    parameters: { term: filterText }
                })
            );

        return this.searchApi
            .searchPeopleAndJobs(search)
            .then((results) => {
                this.possibilities = ImmutableList(
                    results.filter((result) => !this.inGroupSetAlready(result))
                );

                // Include Groups along with the People
                this.possibilities = this.possibilities.concat(matchedGroups);
            })
            .finally(() => {
                this.loadingPossibilities = false;
            });
    }

    private inGroupSetAlready(result: SearchResult): boolean {
        const indexInSet = this.groupSet.find(
            (inSet) => inSet.id === result.id
        );
        return !!indexInSet;
    }
}
